import * as React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';

const StyledBox = styled('div')(({ theme }) => ({
  width: '100%',
  borderRadius: (theme.vars || theme).shape.borderRadius,
  padding: theme.spacing(4),
  backgroundColor: theme.palette.background.default,
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(6),
  },
}));

export default function About() {
  const integrations = [
    'aws',
    'azure',
    'bitbucket',
    'cardano',
    'confluence',
    'discord',
    'ethereum',
    'gcs',
    'github',
    'gitlab',
    'google-drive',
    'jira',
    'onedrive',
    'slack',
    'solana',
    'teams',
    'telegram',
  ];

  return (
    <Box
      id="about"
      sx={(theme) => ({
        width: '100%',
        paddingTop: 8,
        paddingBottom: 8,
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Grid container spacing={4}>
          {/* Left Side: Viva AI Explanation and Icons */}
          <Grid item xs={12} md={6} sx={{marginTop:9}}>
            <Stack spacing={4}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: 'clamp(2rem, 5vw, 2.5rem)',
                  color: 'text.primary',
                }}
              >
                Meet Viva. Your AI Partner.
              </Typography>
              <Typography
                sx={{
                  fontSize: '1rem',
                  color: 'text.secondary',
                  lineHeight: 1.8,
                }}
              >
                Viva is an <strong>autonomous AI agent</strong> designed to bring us closer to <strong>Artificial General Intelligence (AGI)</strong>. Unlike traditional AI, which is limited to generating content, Viva AI integrates with various tools, automating processes across many domains including marketing, blockchain and business operations.
              </Typography>

              {/* Integration Icons */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  marginTop: 4,
                }}
              >
                {/* First Line of Icons */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                  }}
                >
                  {integrations.slice(0, Math.ceil(integrations.length / 2)).map((icon) => (
                    <Box
                      key={icon}
                      component="img"
                      src={`/icons/${icon}.svg`}
                      alt={`${icon} icon`}
                      sx={{
                        width: 32,
                        height: 32,
                        objectFit: 'contain',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.2)',
                        },
                      }}
                    />
                  ))}
                </Box>

                {/* Second Line of Icons */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                  }}
                >
                  {integrations.slice(Math.ceil(integrations.length / 2)).map((icon) => (
                    <Box
                      key={icon}
                      component="img"
                      src={`/icons/${icon}.svg`}
                      alt={`${icon} icon`}
                      sx={{
                        width: 32,
                        height: 32,
                        objectFit: 'contain',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.2)',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Stack>
          </Grid>

          {/* Right Side: The Challenge and Solution */}
          <Grid item xs={12} md={6}>
            <Stack spacing={4}>
              {/* The Challenge */}
              <StyledBox>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    marginBottom: 1,
                  }}
                >
                  The Challenge
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    color: 'text.secondary',
                    lineHeight: 1.8,
                  }}
                >
                  Most AI systems today are limited to generating content, requiring significant human intervention to complete tasks. They lack true autonomy. This creates inefficiencies, fragmented workflows, and added complexity for individuals and businesses alike.
                </Typography>
              </StyledBox>

              {/* How Viva AI Solves It */}
              <StyledBox>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    marginBottom: 1,
                  }}
                >
                  How Viva AI Solves It
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    color: 'text.secondary',
                    lineHeight: 1.8,
                  }}
                >
                  Viva AI is fully autonomous, executing tasks from start to finish without human intervention. From autonomous marketing to blockchain operations to trading analysis, Viva delivers end-to-end solutions by integrating seamlessly with your tools.
                </Typography>
              </StyledBox>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}